import { useSnapshot } from "valtio";
import { stateNavigation, stateOrbitLocation , stateIframeWindow} from "../store";
import { useThree, useFrame, invalidate } from "@react-three/fiber";
import { useRef, useEffect, useState } from "react";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import gsap from "gsap";

// ─── ORBIT LIMITS (global settings) ───────────────────────────────────────────
const ORBIT_H_LIMIT_DEG  = 28;   // horizontal freedom ± from waypoint direction
const ORBIT_V_UP_DEG     = 20;   // upward freedom from waypoint angle
const ORBIT_V_DOWN_DEG   = 40;   // downward freedom from waypoint angle
const WORLD_FLOOR_Y      = 2;    // camera never goes below this world Y position
// ──────────────────────────────────────────────────────────────────────────────

const clearOrbitLimits = (orbitControls) => {
  orbitControls.minAzimuthAngle = -Infinity;
  orbitControls.maxAzimuthAngle =  Infinity;
  orbitControls.minPolarAngle   = 0;
  orbitControls.maxPolarAngle   = Math.PI;
};

const applyOrbitLimits = (orbitControls, camPos, targetPos) => {
  const offset = new THREE.Vector3().subVectors(camPos, targetPos);

  const baseAzimuth = Math.atan2(offset.x, offset.z);
  const hRad = THREE.MathUtils.degToRad(ORBIT_H_LIMIT_DEG);
  orbitControls.minAzimuthAngle = baseAzimuth - hRad;
  orbitControls.maxAzimuthAngle = baseAzimuth + hRad;

  const sph = new THREE.Spherical().setFromVector3(offset);
  const basePhi  = sph.phi;
  const r        = sph.radius;
  const vUpRad   = THREE.MathUtils.degToRad(ORBIT_V_UP_DEG);
  const vDownRad = THREE.MathUtils.degToRad(ORBIT_V_DOWN_DEG);

  // Floor: derived from real world Y — camera must stay above WORLD_FLOOR_Y
  // cos(phi) = (WORLD_FLOOR_Y - target.y) / r  →  phi = acos(...)
  const cosFloor = (WORLD_FLOOR_Y - targetPos.y) / r;
  const floorPhi = Math.acos(Math.max(-1, Math.min(1, cosFloor)));

  orbitControls.minPolarAngle = Math.max(0.01, basePhi - vUpRad);
  orbitControls.maxPolarAngle = Math.max(basePhi, Math.min(floorPhi, basePhi + vDownRad));
};

const CameraWaypointControl = () => {
  const snapStateNavigation = useSnapshot(stateNavigation);
  const snapOrbitCamera = useSnapshot(stateOrbitLocation);
  const camera = useThree((state) => state.camera);
  const orbitRef = useRef();
  const [orbitEnabled, setOrbitEnabled] = useState(true);
  const snapStateIframeWindow = useSnapshot(stateIframeWindow);
  const currentCamPos = useRef(camera.position.clone());
  const currentTargetPos = useRef(new THREE.Vector3(0, 1, 0));
  const isAnimating = useRef(false);

  // Animated proxy — GSAP writes here, useFrame copies to orbitRef.target
  const animTargetProxy = useRef(new THREE.Vector3(0, 1, 0));

  useEffect(() => {
    if (snapStateIframeWindow.LowModelOrbit) {
      setOrbitEnabled(false);
    } else {
      setOrbitEnabled(true);
    }
  }, [snapStateIframeWindow.LowModelOrbit]);

  useEffect(() => {
    setOrbitEnabled(!snapOrbitCamera.lerpRuns);
    if (orbitRef.current) orbitRef.current.enabled = !snapOrbitCamera.lerpRuns;
  }, [snapOrbitCamera.lerpRuns]);

  useEffect(() => {
    camera.position.copy(currentCamPos.current);
    orbitRef.current?.target.copy(currentTargetPos.current);
  }, []);

  const animateTo = (finalCam, finalTarget, duration = 1.5) =>
    new Promise((resolve) => {
      const tl = gsap.timeline({ onComplete: resolve });
      tl.to(camera.position, {
        x: finalCam.x, y: finalCam.y, z: finalCam.z,
        duration, ease: "power1.inOut", delay: 0.2,
        onUpdate: () => invalidate(),
      }, 0);
      tl.to(animTargetProxy.current, {
        x: finalTarget.x, y: finalTarget.y, z: finalTarget.z,
        duration, ease: "power1.inOut", delay: 0.2,
        onUpdate: () => invalidate(),
      }, 0);
    });

  const finishAnimation = (finalCam, finalTarget, disableOrbit = false) => {
    currentCamPos.current.copy(finalCam);
    currentTargetPos.current.copy(finalTarget);
    if (!disableOrbit) {
      applyOrbitLimits(orbitRef.current, finalCam, finalTarget);
      setOrbitEnabled(true);
      orbitRef.current.enabled = true;
    }
    stateNavigation.cameraAnimationRuns = false;
  };

  // Phantom animations
  const runPhantoms = async (queue, pendingWaypoint) => {
    if (!queue || !queue.length) return;
    isAnimating.current = true;
    stateNavigation.cameraAnimationRuns = true;
    clearOrbitLimits(orbitRef.current);
    setOrbitEnabled(false);
    orbitRef.current.enabled = false;

    for (const p of queue) {
      const camTarget   = new THREE.Vector3(...p.Cam.Position);
      const targetTarget = new THREE.Vector3(...p.Target.Position);
      await animateTo(camTarget, targetTarget, 1);
      currentCamPos.current.copy(camTarget);
      currentTargetPos.current.copy(targetTarget);
    }

    if (pendingWaypoint) {
      const finalCam    = new THREE.Vector3(...pendingWaypoint.Cam.Position);
      const finalTarget = new THREE.Vector3(...pendingWaypoint.Target.Position);
      await animateTo(finalCam, finalTarget, 1);
      finishAnimation(finalCam, finalTarget);
    }

    isAnimating.current = false;
  };

  useEffect(() => {
    if (!snapStateNavigation.animatingPhantoms || !snapStateNavigation.phantomQueue.length) return;
    runPhantoms(snapStateNavigation.phantomQueue, snapStateNavigation.pendingWaypoint);
    stateNavigation.phantomQueue = [];
    stateNavigation.animatingPhantoms = false;
    stateNavigation.pendingWaypoint = null;
  }, [snapStateNavigation.animatingPhantoms]);

  // Main waypoint animation
  useEffect(() => {
    if (!orbitRef.current) return;

    const targetCam = snapStateNavigation.wayPointSubClicked
      ? snapStateNavigation.wayCamSubPos
      : snapStateNavigation.wayCamPos;
    const targetTarget = snapStateNavigation.wayPointSubClicked
      ? snapStateNavigation.wayCamTargetSubPos
      : snapStateNavigation.wayCamTargetPos;

    if (!targetCam || !targetTarget || isAnimating.current) return;

    stateNavigation.cameraAnimationRuns = true;
    setOrbitEnabled(false);
    orbitRef.current.enabled = false;
    clearOrbitLimits(orbitRef.current);

    gsap.killTweensOf(camera.position);
    gsap.killTweensOf(animTargetProxy.current);

    animateTo(targetCam, targetTarget, 1.5).then(() => {
      finishAnimation(targetCam, targetTarget);
    });

  }, [snapStateNavigation.wayCamPos, snapStateNavigation.wayPointSubClicked]);

  useFrame(() => {
    if (orbitRef.current) {
      // During animation: keep orbit target in sync with GSAP proxy
      if (snapStateNavigation.cameraAnimationRuns) {
        orbitRef.current.target.copy(animTargetProxy.current);
      }
      orbitRef.current.update();
    }
  });

  return <OrbitControls ref={orbitRef} enableRotate={orbitEnabled} enableZoom={orbitEnabled} />;
};

export default CameraWaypointControl;
